import { useContext, useRef } from 'react'
import {
  ActionDependant,
  GlobalDataContext,
  TemplateContext,
  evaluateTemplateValue,
} from '@ea-lab/reactive-json'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js'
import { Line, Doughnut, Bar, Pie } from 'react-chartjs-2'
import { downloadChartImage } from './chartExport'

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement, ArcElement, BarElement,
  Title, Tooltip, Legend, Filler,
)

const PERCENT_LABEL = new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const RENDERERS = { line: Line, doughnut: Doughnut, pie: Pie, bar: Bar }

/**
 * Instance plugin : au survol d'un segment, estompe les autres séries (baisse
 * leur opacité de dessin), sans muter les couleurs. `hover` mode = point → un
 * seul segment actif ; on mémorise son datasetIndex et on ne redessine que
 * lorsqu'il change (garde anti-boucle). Générique, activé par `fadeOnHover`.
 */
const fadeOnHoverPlugin = {
  id: 'fadeOnHover',
  afterEvent(chart, args) {
    const { type } = args.event
    if (type !== 'mousemove' && type !== 'mouseout') return
    const active = chart.getActiveElements()
    const hovered = (type === 'mouseout' || !active.length) ? null : active[0].datasetIndex
    if (chart.$fadeHovered === hovered) return
    chart.$fadeHovered = hovered
    chart.update('none')
  },
  beforeDatasetDraw(chart, args) {
    const hovered = chart.$fadeHovered
    chart.ctx.save()
    chart.ctx.globalAlpha = (hovered == null || hovered === args.index) ? 1 : 0.2
  },
  afterDatasetDraw(chart) {
    chart.ctx.restore()
  },
}

/**
 * Instance plugin : écrit le TOTAL de chaque pile au-dessus de la barre
 * (histogramme empilé). Chart.js n'a pas de « datalabels » natif et une
 * fonction de dessin ne peut pas vivre en YAML — d'où ce plugin générique,
 * activé par le booléen déclaratif `stackTotals`. Il additionne les séries
 * VISIBLES (il suit donc les bascules de légende) et se cale sur le haut de la
 * barre la plus haute de l'index. Couleur et typo héritées du CSS du canvas
 * (design system) — aucune valeur codée en dur.
 */
const makeStackTotalsPlugin = (valueSuffix) => ({
  id: 'stackTotals',
  afterDatasetsDraw(chart) {
    const { ctx, data } = chart
    if (chart.getDatasetMeta(0)?.type !== 'bar') return
    const count = data.labels?.length ?? 0
    const css = getComputedStyle(chart.canvas)
    ctx.save()
    ctx.font = `700 12px ${css.fontFamily || 'sans-serif'}`
    ctx.fillStyle = css.color || ChartJS.defaults.color
    ctx.textAlign = 'center'
    ctx.textBaseline = 'bottom'
    for (let i = 0; i < count; i += 1) {
      let total = 0
      let top = null
      let x = null
      data.datasets.forEach((ds, di) => {
        if (!chart.isDatasetVisible(di)) return
        const el = chart.getDatasetMeta(di).data[i]
        const v = Number(ds.data?.[i] ?? 0)
        if (!el || !Number.isFinite(v) || v === 0) return
        total += v
        if (top == null || el.y < top) { top = el.y; x = el.x }
      })
      if (top == null || total === 0) continue
      ctx.fillText(fmt(total, valueSuffix), x, top - 6)
    }
    ctx.restore()
  },
})

/**
 * Instance plugin : étiquette chaque part d'un camembert de son pourcentage et
 * de son effectif — « 55,56 % (20) » — posés À L'EXTÉRIEUR, reliés à la part par
 * une ligne de rappel coudée. Chart.js n'a pas de « datalabels » natif et un
 * tracé sur canvas ne peut pas vivre en YAML : d'où ce plugin générique, activé
 * par le booléen déclaratif `sliceLabels`.
 *
 * Les parts nulles sont ignorées (pas d'étiquette fantôme sur une part
 * invisible), et le total est celui des valeurs DESSINÉES, donc les pourcentages
 * somment bien à 100. Couleur et typo héritées du CSS du canvas.
 */
const makeSliceLabelsPlugin = (valueSuffix) => ({
  id: 'sliceLabels',
  afterDatasetsDraw(chart) {
    const { ctx } = chart
    const meta = chart.getDatasetMeta(0)
    if (!meta?.data?.length) return
    const values = (chart.data.datasets[0]?.data ?? []).map((v) => Number(v) || 0)
    const total = values.reduce((a, b) => a + b, 0)
    if (!total) return

    const css = getComputedStyle(chart.canvas)
    ctx.save()
    ctx.font = `600 11px ${css.fontFamily || 'sans-serif'}`
    ctx.fillStyle = css.color || ChartJS.defaults.color
    ctx.strokeStyle = ctx.fillStyle
    ctx.lineWidth = 1
    ctx.textBaseline = 'middle'

    meta.data.forEach((arc, i) => {
      const v = values[i]
      if (!v) return
      const { x, y, startAngle, endAngle, outerRadius } =
        arc.getProps(['x', 'y', 'startAngle', 'endAngle', 'outerRadius'], true)
      const mid = (startAngle + endAngle) / 2
      const cos = Math.cos(mid)
      const sin = Math.sin(mid)
      const toRight = cos >= 0
      // Ligne de rappel : du bord de la part vers l'extérieur, puis un coude
      // horizontal du côté où le texte se lit.
      const elbowX = x + cos * (outerRadius + 12)
      const elbowY = y + sin * (outerRadius + 12)
      const endX = elbowX + (toRight ? 12 : -12)
      ctx.beginPath()
      ctx.moveTo(x + cos * outerRadius, y + sin * outerRadius)
      ctx.lineTo(elbowX, elbowY)
      ctx.lineTo(endX, elbowY)
      ctx.stroke()

      ctx.textAlign = toRight ? 'left' : 'right'
      const pct = PERCENT_LABEL.format((v / total) * 100)
      ctx.fillText(`${pct} % (${fmt(v, valueSuffix)})`, endX + (toRight ? 4 : -4), elbowY)
    })
    ctx.restore()
  },
})

/**
 * Instance plugin : REPÈRES verticaux sur l'axe des catégories (une livraison,
 * une rupture de série). Un trait sur un canvas ne se déclare pas en YAML, et
 * Chart.js n'a pas d'annotation native : d'où ce petit plugin en ligne, sans
 * dépendance ajoutée, activé par la liste déclarative `markers`.
 *
 * Un repère se place par l'INDEX de sa catégorie (`index`), ou par sa clé
 * brute (`at`, rapprochée de `data.rowKeys` puis des libellés). Hors du
 * graphe (index inconnu), il est simplement ignoré.
 *
 * Couleurs : jetons du design system lus sur le canvas (`--chart-marker-<ton>`,
 * repli sur `--chart-marker` puis sur la couleur du texte) — aucune valeur en
 * dur. Le trait ne porte jamais seul l'information : l'appelant liste les mêmes
 * repères en texte sous le graphe.
 */
const makeMarkersPlugin = (markers) => ({
  id: 'markers',
  afterDatasetsDraw(chart) {
    const { ctx, chartArea, data, scales } = chart
    const axis = scales.x
    if (!axis || !chartArea || !markers.length) return
    const css = getComputedStyle(chart.canvas)
    const token = (tone) => css.getPropertyValue(`--chart-marker-${tone}`).trim()
      || css.getPropertyValue('--chart-marker').trim()
      || css.color
    const surface = css.getPropertyValue('--chart-marker-ink').trim() || '#fff'
    const indexOf = (m) => {
      if (Number.isInteger(m.index)) return m.index
      const key = m.at == null ? '' : String(m.at)
      const byKey = (data.rowKeys ?? []).indexOf(key)
      return byKey >= 0 ? byKey : (data.labels ?? []).indexOf(key)
    }
    // Plusieurs repères sur la même catégorie : leurs étiquettes s'empilent
    // vers la droite au lieu de se recouvrir.
    const seen = new Map()
    ctx.save()
    ctx.font = `700 10px ${css.fontFamily || 'sans-serif'}`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    markers.forEach((m) => {
      const i = indexOf(m)
      if (i < 0 || i >= (data.labels?.length ?? 0)) return
      const x = axis.getPixelForValue(i)
      const rank = seen.get(i) ?? 0
      seen.set(i, rank + 1)
      const color = token(m.tone)
      ctx.strokeStyle = color
      ctx.lineWidth = 1.4
      ctx.setLineDash(m.tone === 'delivery' ? [4, 3] : [1, 3])
      ctx.beginPath()
      ctx.moveTo(x, chartArea.top - 4)
      ctx.lineTo(x, chartArea.bottom)
      ctx.stroke()
      ctx.setLineDash([])
      if (m.tag) {
        const w = Math.max(18, ctx.measureText(String(m.tag)).width + 8)
        const bx = x - w / 2 + rank * (w + 2)
        ctx.fillStyle = color
        ctx.fillRect(bx, chartArea.top - 20, w, 14)
        ctx.fillStyle = surface
        ctx.fillText(String(m.tag), bx + w / 2, chartArea.top - 13)
      }
    })
    ctx.restore()
  },
})

/**
 * Generic Chart.js renderer bound to a `{labels, datasets}` data object —
 * exactly the shape produced server-side / by a dataProcessor, so the YAML
 * stays declarative and a page never has to assemble datasets in JS.
 *
 * Chart.js options that need JS functions (number formatters, tooltip
 * callbacks) can't live in YAML, so they're driven here by simple declarative
 * props instead of raw option objects:
 *   - legendPosition   : 'bottom' | 'right' | 'top' | 'left' | 'none'
 *   - valueSuffix      : appended to Y ticks + tooltip values (e.g. ' €').
 *   - precisionZero    : integer-only Y ticks (no decimals).
 *   - beginAtZero      : force the Y axis to start at 0 (default true).
 *   - richTooltip      : tooltip « table » sur toute la colonne (mode index) —
 *                        trié décroissant, valeurs nulles masquées, ligne Total.
 *                        Évite d'avoir à relier une couleur à la légende.
 *   - seriesGroupLabel : suffixe ajouté à chaque ligne du tooltip, ex. le nom
 *                        du pipeline → « 1. Besoin détecté (Pipeline New Business) ».
  *   - fadeOnHover      : estompe les segments non survolés (highlight façon HubSpot).
 *   - stacked          : empile les séries (barres) — pose `stacked` sur X et Y.
 *   - horizontal       : barres COUCHÉES (catégories en ordonnée). Les réglages
 *                        de valeur (suffixe, graduation) suivent l'axe des
 *                        valeurs, qui devient l'axe des abscisses.
 *   - xAxisTitle       : intitulé sous l'axe des abscisses (ex. « Date de création — Mensuel »).
 *   - xTickUnit        : 'year' | 'month' — graduation de l'axe des DATES plus large que
 *                        le pas des points : un repère par année (« 2026 ») ou par mois
 *                        (« 10/2026 ») au premier point de la période, à l'horizontale ;
 *                        les points gardent leur pas (jour), l'infobulle sa date exacte.
 *   - yAxisTitle       : intitulé le long de l'axe des ordonnées (ex. « Nombre de transactions »).
 *   - stackTotals      : écrit le total de chaque pile au-dessus de la barre.
 *   - sliceLabels      : « 55,56 % (20) » à l'extérieur de chaque part d'un
 *                        camembert, relié par une ligne de rappel.
 *   - markers          : repères verticaux `[{ index | at, label, tag, tone }]`
 *                        (livraison, rupture de série) — cf. makeMarkersPlugin.
 *                        À défaut, ceux que porte `data.markers`.
 *   - valueMin / valueMax : bornes de l'axe des valeurs (un score se lit de 0 à 100).
 *   - exportable       : ajoute un bouton « Télécharger l'image » sous le graphe
 *                        (`toBase64Image()` du canvas) ; `exportName` nomme le fichier.
 * An optional `options` prop is shallow-merged last for any escape-hatch tweak.
 * ⚠️ Il REMPLACE la clé qu'il porte (merge de surface) : préférer les props
 * déclaratives ci-dessus, qui se composent dans `buildOptions`.
 *
 * YAML usage:
 *   - type: ChartJs
 *     chartType: bar
 *     data: ~~.pipeline.charts.byStageAmountPerMonth
 *     richTooltip: true
 *     fadeOnHover: true
 *     seriesGroupLabel: "Pipeline New Business"
 *     valueSuffix: " €"
 */
export const ChartJs = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const resolve = (v) => evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })

  const chartType = resolve(props.chartType) || 'line'
  const Renderer = RENDERERS[chartType] ?? Line
  const data = resolve(props.data) || { labels: [], datasets: [] }
  const legendPosition = resolve(props.legendPosition)
  const valueSuffix = resolve(props.valueSuffix)
  const precisionZero = resolve(props.precisionZero)
  const beginAtZero = resolve(props.beginAtZero)
  const richTooltip = resolve(props.richTooltip)
  const seriesGroupLabel = joinLabels(resolve(props.seriesGroupLabel))
  const fadeOnHover = resolve(props.fadeOnHover)
  const stacked = resolve(props.stacked)
  const horizontal = resolve(props.horizontal)
  const xAxisTitle = resolve(props.xAxisTitle)
  const xTickUnit = resolve(props.xTickUnit)
  const yAxisTitle = resolve(props.yAxisTitle)
  const stackTotals = resolve(props.stackTotals)
  const sliceLabels = resolve(props.sliceLabels)
  const rawMarkers = resolve(props.markers) ?? data?.markers
  const markers = Array.isArray(rawMarkers) ? rawMarkers.filter((m) => m && typeof m === 'object') : []
  const valueMin = resolve(props.valueMin)
  const valueMax = resolve(props.valueMax)
  const exportable = resolve(props.exportable)
  const exportName = resolve(props.exportName) || 'graphique'
  const exportLabel = resolve(props.exportLabel) || 'Télécharger l’image'
  // L'appelant (ChartLoader) peut fournir sa propre ref pour poser le bouton
  // d'export HORS de la zone `role="img"` du graphe.
  const ownRef = useRef(null)
  const chartRef = props.chartRef ?? ownRef
  const optionsOverride = resolve(props.options)
  const wrapperClass = resolve(props.wrapperClass) ?? 'relative h-72'

  const options = buildOptions({
    chartType, legendPosition, valueSuffix, precisionZero, beginAtZero,
    richTooltip, seriesGroupLabel, fadeOnHover, stacked, horizontal, xAxisTitle, yAxisTitle,
    stackTotals, sliceLabels, valueMin, valueMax, hasMarkers: markers.length > 0, xTickUnit,
  })
  const merged = optionsOverride && typeof optionsOverride === 'object'
    ? { ...options, ...optionsOverride }
    : options

  // Clic sur un segment. Chart.js ne donne que des INDEX (élément et série) :
  // c'est l'appelant qui sait ce qu'ils désignent, d'où un rappel à deux index
  // plutôt qu'un événement métier. `onClick` est posé APRÈS la fusion des
  // options pour qu'un `options:` de YAML ne le fasse pas disparaître en
  // silence, et le curseur suit — sans quoi rien n'indique que c'est cliquable.
  const onSegmentClick = props.onSegmentClick
  const finalOptions = typeof onSegmentClick === 'function'
    ? {
      ...merged,
      onClick: (event, elements, chart) => {
        // Les `elements` fournis suivent le mode d'INTERACTION du graphe, qui
        // est celui du survol : en mode « index », c'est toute la rangée, et le
        // premier élément est toujours celui de la première série — on ouvrait
        // donc le détail d'une autre barre que celle cliquée. Le clic, lui,
        // désigne un point précis : on redemande l'élément SOUS le curseur.
        const hits = chart?.getElementsAtEventForMode
          ? chart.getElementsAtEventForMode(event, 'nearest', { intersect: true }, true)
          : elements
        const hit = hits?.[0] ?? elements?.[0]
        if (hit) onSegmentClick(hit.index, hit.datasetIndex)
      },
      onHover: (event, elements, chart) => {
        const target = event?.native?.target
        if (!target) return
        // Même règle que le clic : la main ne s'affiche que SUR une barre, pas
        // partout où le survol « index » attrape la rangée entière.
        const hits = chart?.getElementsAtEventForMode
          ? chart.getElementsAtEventForMode(event, 'nearest', { intersect: true }, true)
          : elements
        target.style.cursor = hits?.length ? 'pointer' : 'default'
      },
    }
    : merged

  const instancePlugins = [
    ...(fadeOnHover ? [fadeOnHoverPlugin] : []),
    ...(stackTotals ? [makeStackTotalsPlugin(valueSuffix)] : []),
    ...(sliceLabels ? [makeSliceLabelsPlugin(valueSuffix)] : []),
    ...(markers.length ? [makeMarkersPlugin(markers)] : []),
  ]

  const download = () => downloadChartImage(chartRef.current, exportName)

  const chart = (
    <div className={wrapperClass}>
      <Renderer ref={chartRef} data={data} options={finalOptions} plugins={instancePlugins} />
    </div>
  )

  return (
    <ActionDependant {...props}>
      {exportable ? (
        <>
          {chart}
          <div className="chart-actions no-print">
            <button type="button" className="btn btn-ghost btn-sm" onClick={download}>{exportLabel}</button>
          </div>
        </>
      ) : chart}
    </ActionDependant>
  )
}

/** Formats a numeric value the French way, with an optional unit suffix. */
function fmt(value, suffix) {
  const n = value?.toLocaleString('fr-FR') ?? '—'
  return `${n}${suffix ?? ''}`
}

const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
))

/**
 * External (HTML) tooltip handler for `richTooltip` : rend un tableau DOM (une
 * ligne par série, pastille + nom + valeur), **en gras la ligne survolée**, trié
 * décroissant, zéros masqués, ligne Total. Positionné du côté OPPOSÉ au curseur
 * pour ne pas recouvrir la barre lue. Réutilisable par n'importe quel chart.
 */
function makeExternalTooltip({ valueSuffix, seriesGroupLabel, horizontal }) {
  // La VALEUR d'un point se lit sur l'axe des valeurs : `y` debout, `x` couché
  // (`parsed.y` y vaut alors le rang de la catégorie, pas la mesure).
  const valueOfPoint = (p) => (horizontal ? p.parsed.x : p.parsed.y) ?? 0;
  return (context) => {
    const { chart, tooltip } = context
    const parent = chart.canvas.parentNode
    let el = parent.querySelector(':scope > .rj-chart-tooltip')
    if (!el) {
      el = document.createElement('div')
      el.className = 'rj-chart-tooltip'
      Object.assign(el.style, {
        position: 'absolute', pointerEvents: 'none', zIndex: '30',
        background: 'rgba(15,23,42,0.96)', color: '#fff', borderRadius: '10px',
        padding: '10px 12px', fontSize: '12px', lineHeight: '1.55',
        boxShadow: '0 10px 30px rgba(0,0,0,0.28)', maxWidth: '360px',
        transition: 'opacity 120ms ease', opacity: '0',
      })
      parent.appendChild(el)
    }
    if (tooltip.opacity === 0) { el.style.opacity = '0'; return }

    const hoveredDs = (chart.getActiveElements?.() || [])[0]?.datasetIndex ?? null
    const points = (tooltip.dataPoints || [])
      .filter((p) => valueOfPoint(p) > 0)
      .sort((a, b) => valueOfPoint(b) - valueOfPoint(a))
    const title = tooltip.title?.[0] ?? ''
    let total = 0
    const rows = points.map((p) => {
      total += valueOfPoint(p)
      const name = p.dataset.label ?? ''
      const full = seriesGroupLabel ? `${name} (${seriesGroupLabel})` : name
      const weight = p.datasetIndex === hoveredDs ? '700' : '400'
      return `<div style="display:flex;align-items:center;gap:6px;font-weight:${weight}">`
        + `<span style="width:10px;height:10px;border-radius:2px;flex:0 0 auto;background:${p.dataset.backgroundColor}"></span>`
        + `<span style="flex:1">${escapeHtml(full)}</span>`
        + `<span style="margin-left:14px;font-variant-numeric:tabular-nums">${escapeHtml(fmt(valueOfPoint(p), valueSuffix))}</span>`
        + `</div>`
    }).join('')
    el.innerHTML = `<div style="font-weight:600;margin-bottom:6px">${escapeHtml(title)}</div>${rows}`
      + `<div style="border-top:1px solid rgba(255,255,255,0.16);margin-top:6px;padding-top:6px;`
      + `font-weight:600;display:flex;justify-content:space-between;gap:14px">`
      + `<span>Total</span><span style="font-variant-numeric:tabular-nums">${escapeHtml(fmt(total, valueSuffix))}</span></div>`

    // Positionnement : côté opposé au curseur, clampé dans le canvas.
    const area = chart.chartArea
    const mid = (area.left + area.right) / 2
    const w = el.offsetWidth
    const h = el.offsetHeight
    let left = tooltip.caretX < mid ? tooltip.caretX + 22 : tooltip.caretX - w - 22
    left = Math.max(area.left, Math.min(left, chart.width - w - 4))
    const top = Math.max(4, Math.min(tooltip.caretY - h / 2, chart.height - h - 4))
    el.style.left = `${left}px`
    el.style.top = `${top}px`
    el.style.opacity = '1'
  }
}

/**
 * Aplati un libellé de groupe qui peut venir d'un filtre MULTI-valeurs : chaîne,
 * tableau de chaînes, ou tableau d'enveloppes `{ value, label }` (forme de
 * l'éditeur multi-sélection `es-select`). Un tableau vide rend `''` — un `[]`
 * étant truthy en JS, il afficherait sinon un suffixe « () » vide.
 */
function joinLabels(v) {
  if (!Array.isArray(v)) return v
  return v
    .map((e) => (e && typeof e === 'object' ? (e.label ?? e.value) : e))
    .filter((e) => e != null && e !== '')
    .join(', ')
}

/**
 * La période (année « 2026 », mois « 10/2026 ») d'un libellé de date tel que le
 * met en forme Elasticsearch : « 21/10/2026 », « 10/2026 », « 2026 » ou ISO
 * « 2026-10-21 ». Rien de reconnaissable : null (pas de repère).
 */
function periodOf(label, unit) {
  const s = String(label ?? '')
  const iso = s.match(/(\d{4})-(\d{2})/)
  const fr = s.match(/(?:^|\D)(\d{2})\/(\d{4})(?!\d)/)
  const year = iso?.[1] ?? fr?.[2] ?? s.match(/\d{4}/)?.[0]
  if (!year) return null
  if (unit === 'year') return year
  const month = iso?.[2] ?? fr?.[1]
  return month ? `${month}/${year}` : null
}

/** Builds Chart.js options from the declarative props (no YAML-side functions). */
function buildOptions({
  chartType, legendPosition, valueSuffix, precisionZero, beginAtZero,
  richTooltip, seriesGroupLabel, fadeOnHover, stacked, horizontal, xAxisTitle, yAxisTitle,
  stackTotals, sliceLabels, valueMin, valueMax, hasMarkers, xTickUnit,
}) {
  const base = { responsive: true, maintainAspectRatio: false }

  // Circular charts: only a legend knob, no cartesian axes.
  if (chartType === 'doughnut' || chartType === 'pie') {
    return {
      ...base,
      plugins: { legend: legendBlock(legendPosition ?? 'right') },
      // Les étiquettes de part se dessinent HORS du cercle : sans marge, elles
      // seraient rognées par le bord du canvas.
      ...(sliceLabels ? { layout: { padding: 56 } } : {}),
    }
  }

  const plugins = { legend: legendBlock(legendPosition ?? 'bottom') }

  if (richTooltip) {
    // Tooltip « tableau » HTML externe : ligne survolée en gras, positionné à
    // l'opposé du curseur (ne recouvre pas la barre lue). Voir makeExternalTooltip.
    plugins.tooltip = {
      enabled: false,
      mode: 'index',
      intersect: false,
      axis: horizontal ? 'y' : 'x',
      external: makeExternalTooltip({ valueSuffix, seriesGroupLabel, horizontal }),
    }
  } else if (valueSuffix) {
    plugins.tooltip = {
      axis: horizontal ? 'y' : 'x',
      callbacks: { label: (c) => fmt(horizontal ? c.parsed.x : c.parsed.y, valueSuffix) },
    }
  }

  let ticks = {}
  if (valueSuffix) ticks = { callback: (v) => `${v}${valueSuffix}` }
  else if (precisionZero) ticks = { precision: 0 }

  // `fadeOnHover` veut un seul segment actif au survol (mode point) pour savoir
  // lequel garder net ; le tooltip reste en mode index (table), défini plus haut.
  // `axis` : en barres COUCHÉES, la rangée se désigne par l'ordonnée — sans lui,
  // Chart.js cherche l'index sur l'abscisse et le survol attrape n'importe quoi.
  const interaction = fadeOnHover
    ? { mode: 'point', intersect: true }
    : { mode: 'index', intersect: false, axis: horizontal ? 'y' : 'x' }

  // Échelles composées ICI (et pas via `options`) : le merge de surface de
  // l'escape hatch remplacerait `scales` en bloc, et le formatage des ticks
  // (valueSuffix) serait perdu dès qu'un graphe est empilé.
  // Couché : l'axe des VALEURS passe en abscisse — c'est lui qui porte la
  // graduation et le suffixe, sans quoi les montants iraient décorer l'axe des
  // catégories.
  const value = { stacked: !!stacked, beginAtZero: beginAtZero !== false, ticks }
  // Bornes déclarées : un score se lit sur une échelle FIXE (0 à 100). Laissée
  // libre, l'échelle se recale sur les valeurs et deux graphes voisins ne se
  // comparent plus à l'œil.
  if (valueMin !== undefined && valueMin !== null && valueMin !== '') value.min = Number(valueMin)
  if (valueMax !== undefined && valueMax !== null && valueMax !== '') value.max = Number(valueMax)
  const category = { stacked: !!stacked }
  const x = horizontal ? value : category
  const y = horizontal ? category : value
  if (xAxisTitle) x.title = { display: true, text: xAxisTitle }
  if (!horizontal && (xTickUnit === 'year' || xTickUnit === 'month')) {
    // Un repère par période, au premier point qui y tombe : la catégorie dont la
    // période diffère de celle du point précédent. Plus de saut automatique (il
    // tomberait au hasard des dates), et la grille ne marque que ces repères.
    // Place comptée : un repère sur k quand la largeur ne les loge pas tous
    // (2011, 2013, 2015… sur un graphe étroit), et pas de repère pour une
    // première période tronquée (série commencée en mai) — il collerait au suivant.
    const minGap = xTickUnit === 'year' ? 44 : 64
    x.ticks = {
      autoSkip: false,
      maxRotation: 0,
      callback(v) {
        const labels = this.chart.data.labels || []
        if (this._orbitPeriods?.labels !== labels) {
          const starts = []
          labels.forEach((l, i) => {
            const p = periodOf(l, xTickUnit)
            if (p && p !== (i ? periodOf(labels[i - 1], xTickUnit) : null)) starts.push(i)
          })
          // Première période tronquée : moins de la moitié d'une période pleine.
          const full = starts.length > 2 ? (starts[starts.length - 1] - starts[1]) / (starts.length - 2) : 0
          if (full && starts[1] - starts[0] < full / 2) starts.shift()
          this._orbitPeriods = { labels, starts: new Map(starts.map((at, n) => [at, n])) }
        }
        const n = this._orbitPeriods.starts.get(v)
        if (n === undefined) return ''
        const k = Math.max(1, Math.ceil((this._orbitPeriods.starts.size * minGap) / (this.chart.chartArea?.width || this.chart.width || 1)))
        return n % k === 0 ? periodOf(labels[v], xTickUnit) : ''
      },
    }
    x.grid = { color: (c) => (c.tick?.label ? 'rgba(0, 0, 0, 0.1)' : 'transparent') }
  }
  if (yAxisTitle) y.title = { display: true, text: yAxisTitle }

  return {
    ...base,
    ...(horizontal ? { indexAxis: 'y' } : {}),
    interaction,
    plugins,
    scales: { x, y },
    // Le total d'une pile — comme l'étiquette d'un repère — se dessine
    // AU-DESSUS de la zone de tracé : sans marge haute il serait rogné par le
    // bord du canvas.
    ...(stackTotals || hasMarkers ? { layout: { padding: { top: hasMarkers ? 24 : 20 } } } : {}),
  }
}

function legendBlock(position) {
  if (position === 'none') return { display: false }
  return { position, labels: { boxWidth: 12, usePointStyle: true } }
}
